import { useEffect, useMemo, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, SkipForward, EyeOff, Loader2, ImageIcon, CheckCircle2, X, Layers, Package, Square } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useRapidPhotoQueue } from '@/hooks/useRapidPhotoQueue';
import { useUIStore } from '@/store/uiStore';
import { IMAGE_ACCEPT } from '@/lib/photoInputUtils';
import { cn } from '@/lib/utils';
import { formatGradingCompany } from '@/constants/gradingCompanies';
import type { UserItem } from '@/types/userItem';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All the seller's items — sheet filters to items lacking real photos. */
  allItems: UserItem[];
  /** Optional escape hatch: parent handles switching to the gallery uploader. */
  onSwitchToGallery?: () => void;
}

type Bucket = 'single' | 'slab' | 'sealed';
const BUCKETS: { id: Bucket; label: string; icon: typeof Square }[] = [
  { id: 'single', label: 'Singles', icon: Square },
  { id: 'slab', label: 'Slabs', icon: Layers },
  { id: 'sealed', label: 'Sealed', icon: Package },
];

export function RapidPhotographSheet({ open, onOpenChange, allItems, onSwitchToGallery }: Props) {
  const isMobile = useIsMobile();
  const { requestBulkPhotoUpload } = useUIStore();

  const photoless = useMemo(
    () => allItems.filter((it) => !it.images || it.images.length === 0),
    [allItems],
  );

  const bucketCounts = useMemo(() => {
    const counts: Record<Bucket, number> = { single: 0, slab: 0, sealed: 0 };
    for (const it of photoless) {
      const t = (it as any).sc_product_type as Bucket | undefined;
      if (t && t in counts) counts[t]++;
    }
    return counts;
  }, [photoless]);

  const [mode, setMode] = useState<'guided' | 'gallery'>('guided');
  const [bucket, setBucket] = useState<Bucket | null>(null);

  useEffect(() => {
    if (open) {
      setMode('guided');
      setBucket(null);
    }
  }, [open]);

  const filteredItems = useMemo(
    () => (bucket ? photoless.filter((it) => (it as any).sc_product_type === bucket) : []),
    [photoless, bucket],
  );

  const queue = useRapidPhotoQueue({ items: filteredItems, open: open && !!bucket });

  const close = () => {
    queue.finish();
    onOpenChange(false);
  };

  const switchToGallery = () => {
    if (onSwitchToGallery) {
      onSwitchToGallery();
      return;
    }
    onOpenChange(false);
    setTimeout(() => requestBulkPhotoUpload(), 180);
  };

  const body = (
    <RapidPhotographBody
      queue={queue}
      mode={mode}
      onModeChange={(m) => {
        if (m === 'gallery') switchToGallery();
        else setMode('guided');
      }}
      bucket={bucket}
      bucketCounts={bucketCounts}
      onBucketChange={setBucket}
      totalPhotoless={photoless.length}
      onClose={close}
    />
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="bg-chrome border-white/[0.06] rounded-t-2xl p-0 max-h-[92dvh] flex flex-col"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Rapid Photograph</SheetTitle>
            <SheetDescription>Snap front + back photos for each of your items.</SheetDescription>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-chrome border-white/[0.06] p-0 max-w-lg w-[min(560px,95vw)] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="sr-only">
          <DialogTitle>Rapid Photograph</DialogTitle>
          <DialogDescription>Snap front + back photos for each of your items.</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

interface BodyProps {
  queue: ReturnType<typeof useRapidPhotoQueue>;
  mode: 'guided' | 'gallery';
  onModeChange: (m: 'guided' | 'gallery') => void;
  bucket: Bucket | null;
  bucketCounts: Record<Bucket, number>;
  onBucketChange: (b: Bucket | null) => void;
  totalPhotoless: number;
  onClose: () => void;
}

function RapidPhotographBody({
  queue,
  mode,
  onModeChange,
  bucket,
  bucketCounts,
  onBucketChange,
  totalPhotoless,
  onClose,
}: BodyProps) {
  const { current, index, total, pending, saving, isDone, summary } = queue;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const previews = useMemo(() => pending.map((f) => URL.createObjectURL(f)), [pending]);
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) queue.addFiles(files);
    e.target.value = '';
  };

  const activeBucketLabel = bucket ? BUCKETS.find((b) => b.id === bucket)?.label : null;

  return (
    <>
      {/* Header — no divider */}
      <div className="px-4 pt-4 pb-3 shrink-0">
        <div className="text-base font-semibold text-foreground">Bulk photograph</div>
        {bucket && total > 0 && !isDone ? (
          <button
            type="button"
            onClick={() => onBucketChange(null)}
            className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="text-active-gold font-medium">{activeBucketLabel}</span>
            <span>·</span>
            <span>Item {Math.min(index + 1, total)} of {total}</span>
            <span className="opacity-60">· Change</span>
          </button>
        ) : (
          <div className="mt-1 text-[11px] text-muted-foreground">
            Snap front + back for photoless items.
          </div>
        )}

        {/* Prominent mode toggle */}
        <div className="mt-3 grid grid-cols-2 gap-1 rounded-full bg-white/[0.05] p-1">
          <button
            type="button"
            onClick={() => onModeChange('guided')}
            className={cn(
              'h-9 rounded-full text-sm font-medium transition-colors',
              mode === 'guided'
                ? 'bg-active-gold text-black'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Guided
          </button>
          <button
            type="button"
            onClick={() => onModeChange('gallery')}
            className={cn(
              'h-9 rounded-full text-sm font-medium transition-colors',
              'text-muted-foreground hover:text-foreground',
            )}
          >
            From gallery
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {bucket && total > 0 && !isDone && (
        <div className="h-1 bg-white/[0.05] shrink-0">
          <div
            className="h-full bg-active-gold/70 transition-all duration-300"
            style={{ width: `${(index / total) * 100}%` }}
          />
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {totalPhotoless === 0 ? (
          <EmptyState onClose={onClose} />
        ) : !bucket ? (
          <BucketPicker counts={bucketCounts} onPick={onBucketChange} />
        ) : isDone ? (
          <DoneState summary={summary} onClose={onClose} onChangeBucket={() => onBucketChange(null)} />
        ) : total === 0 ? (
          <EmptyBucketState label={activeBucketLabel ?? ''} onChangeBucket={() => onBucketChange(null)} />
        ) : current ? (
          <>
            {/* Reference card */}
            <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/[0.06] p-3">
              <div className="w-14 h-20 rounded-md overflow-hidden bg-white/[0.05] shrink-0 flex items-center justify-center">
                {current.stock_image_url ? (
                  <img src={current.stock_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{current.title}</div>
                <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                  {[current.set_name, current.card_number && `#${current.card_number}`, current.grading_company && `${formatGradingCompany(current.grading_company)} ${current.slab_grade ?? ''}`.trim()]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </div>
                {current.stock_image_url && (
                  <div className="text-[10px] text-muted-foreground mt-1">Stock reference — capture your actual card</div>
                )}
              </div>
            </div>

            {/* Captured thumbnails */}
            <div className="grid grid-cols-4 gap-2">
              {pending.map((f, i) => (
                <div key={`${i}-${f.name}-${f.size}`} className="relative aspect-[3/4] rounded-md overflow-hidden bg-white/[0.04] group">
                  <img src={previews[i]} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => queue.removePending(i)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white inline-flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    aria-label="Remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <div className="absolute bottom-0.5 left-0.5 text-[9px] text-white/85 bg-black/50 px-1 rounded">
                    {i === 0 ? 'Front' : i === 1 ? 'Back' : `#${i + 1}`}
                  </div>
                </div>
              ))}
              {pending.length < 10 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-[3/4] rounded-md border border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.05] hover:border-active-gold/40 transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  <Camera className="w-5 h-5" />
                  <span className="text-[10px] font-medium">
                    {pending.length === 0 ? 'Front' : pending.length === 1 ? 'Back' : 'Add'}
                  </span>
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={IMAGE_ACCEPT}
              capture="environment"
              multiple
              hidden
              onChange={onPick}
            />

            <div className="text-[11px] text-muted-foreground">
              Front, then back. Up to 10.
            </div>
          </>
        ) : null}
      </div>

      {/* Footer actions — only when actively capturing */}
      {bucket && !isDone && total > 0 && (
        <div className="border-t border-white/[0.06] p-3 flex items-center gap-2 shrink-0 pb-[max(env(safe-area-inset-bottom),12px)]">
          <Button
            variant="ghost"
            size="sm"
            onClick={queue.noCard}
            disabled={saving}
            className="text-muted-foreground"
          >
            <EyeOff className="w-3.5 h-3.5 mr-1" /> No card
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={queue.skip}
            disabled={saving}
            className="text-muted-foreground"
          >
            <SkipForward className="w-3.5 h-3.5 mr-1" /> Skip
          </Button>
          <div className="flex-1" />
          <Button
            size="sm"
            onClick={queue.saveAndNext}
            disabled={pending.length === 0 || saving}
            className="bg-active-gold text-black hover:bg-active-gold/90 min-w-[110px]"
          >
            {saving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…
              </>
            ) : (
              <>Save & next</>
            )}
          </Button>
        </div>
      )}
    </>
  );
}

function BucketPicker({
  counts,
  onPick,
}: {
  counts: Record<Bucket, number>;
  onPick: (b: Bucket) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="text-sm text-foreground">What are you photographing right now?</div>
      <div className="text-[11px] text-muted-foreground -mt-1">
        Pick one so the queue stays focused — no jumping between a booster box and a slab.
      </div>
      <div className="grid grid-cols-3 gap-2 pt-1">
        {BUCKETS.map(({ id, label, icon: Icon }) => {
          const count = counts[id];
          const disabled = count === 0;
          return (
            <button
              key={id}
              type="button"
              disabled={disabled}
              onClick={() => onPick(id)}
              className={cn(
                'rounded-xl border p-3 flex flex-col items-center gap-2 transition-all',
                disabled
                  ? 'border-white/[0.04] bg-white/[0.02] text-muted-foreground/50 cursor-not-allowed'
                  : 'border-white/[0.08] bg-white/[0.03] hover:border-active-gold/50 hover:bg-active-gold/[0.05] text-foreground',
              )}
            >
              <Icon className="w-5 h-5" />
              <div className="text-sm font-medium">{label}</div>
              <div className={cn('text-[11px]', disabled ? '' : 'text-active-gold')}>
                {count} left
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EmptyBucketState({ label, onChangeBucket }: { label: string; onChangeBucket: () => void }) {
  return (
    <div className="text-center py-10 space-y-3">
      <div className="text-sm text-foreground">No {label.toLowerCase()} need photos.</div>
      <Button size="sm" variant="outline" onClick={onChangeBucket}>Pick another type</Button>
    </div>
  );
}

function EmptyState({ onClose }: { onClose: () => void }) {
  return (
    <div className="text-center py-10 space-y-3">
      <div className="text-sm text-foreground">You're all caught up.</div>
      <div className="text-xs text-muted-foreground">Every item in view has at least one photo.</div>
      <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
    </div>
  );
}

function DoneState({
  summary,
  onClose,
  onChangeBucket,
}: {
  summary: { captured: number; skipped: number; nocard: number };
  onClose: () => void;
  onChangeBucket: () => void;
}) {
  return (
    <div className="text-center py-10 space-y-4">
      <CheckCircle2 className="w-10 h-10 mx-auto text-active-gold" />
      <div className="text-sm font-semibold">Session complete</div>
      <div className="text-xs text-muted-foreground">
        {summary.captured} captured · {summary.skipped} skipped · {summary.nocard} no card on hand
      </div>
      <div className="flex gap-2 justify-center">
        <Button size="sm" variant="outline" onClick={onChangeBucket}>Photograph another type</Button>
        <Button size="sm" onClick={onClose} className="bg-active-gold text-black hover:bg-active-gold/90">
          Done
        </Button>
      </div>
    </div>
  );
}
